import { cn } from "@app/lib/utils";
import type { FootprintCountry } from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

type CountryPanelProps = {
  country: FootprintCountry;
  /** Every country, in order — drives the pager at the foot of the panel. */
  countries: FootprintCountry[];
  onClose: () => void;
  onSelect: (key: string) => void;
  className?: string;
};

export function CountryPanel({
  country,
  countries,
  onClose,
  onSelect,
  className,
}: CountryPanelProps) {
  const index = countries.findIndex(({ key }) => key === country.key);

  // The pager wraps, so there is always somewhere to go next.
  const step = (offset: number) =>
    onSelect(
      countries[(index + offset + countries.length) % countries.length].key,
    );

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label={trans(country.name)}
      className={cn(
        "relative overflow-hidden rounded-2xl border border-white/10 bg-[#040e28]/95 p-6 shadow-2xl backdrop-blur-md sm:p-7",
        className,
      )}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={trans("globalFootprint.closePanel")}
        className="absolute end-4 top-4 rounded-md p-1 text-white/50 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bright-orange"
      >
        <X className="size-5" strokeWidth={2} aria-hidden />
      </button>

      <p className="text-[0.625rem] font-semibold uppercase tracking-[0.2em] text-white/45">
        {trans("globalFootprint.about")}
      </p>

      <h3 className="mt-2 pe-8 text-xl font-bold leading-snug text-white sm:text-2xl">
        {trans("senaat")}{" "}
        <span className="text-bright-orange">— {trans(country.name)}</span>
      </h3>

      <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-[0.9375rem]">
        {trans(country.body)}
      </p>

      {/* Pager — step through the countries without going back to the globe.
          The chevrons flip in RTL so "next" always points forwards. */}
      <div className="mt-6 flex items-center justify-between gap-3 border-t border-white/10 pt-5">
        <button
          type="button"
          onClick={() => step(-1)}
          className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs font-medium text-white/65 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bright-orange"
        >
          <ChevronLeft
            className="size-4 shrink-0 rtl:rotate-180"
            strokeWidth={2}
            aria-hidden
          />
          {trans("globalFootprint.previousCountry")}
        </button>

        <ul className="flex items-center gap-2">
          {countries.map(({ key, name }) => (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(key)}
                aria-label={trans(name)}
                aria-current={key === country.key}
                className={cn(
                  "block size-2.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bright-orange focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
                  key === country.key
                    ? "bg-bright-orange"
                    : "bg-white/25 hover:bg-white/50",
                )}
              />
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => step(1)}
          className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs font-medium text-white/65 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bright-orange"
        >
          {trans("globalFootprint.nextCountry")}
          <ChevronRight
            className="size-4 shrink-0 rtl:rotate-180"
            strokeWidth={2}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
